import { Typography } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { DeleteDialog } from 'common/components/deleteDialog/DeleteDialog';
import {
    selectCurrentEntityType,
    selectDeleteCurrentTenantId,
    setCurrentEntityType,
} from 'common/components/deleteDialog/deleteDialogSlice';
import { useDeleteDialogHandler } from 'common/hooks/useDeleteDialogHandler';
import { deleteUserProfileClaims } from 'common/services/api/UserProfileClaims/UserProfileClaims.thunk';
import { DeleteDialogContentProps, DrawerEntitiesNames } from 'common/types/TypeDefs';
import { DEFAULT_ERROR_MESSAGE } from 'common/utils/errorHandlers';
import { updateUserProfileTenant } from 'features/userProfileApps/helpers';
import { selectUserDetails } from 'features/users/usersSlice';
import { useSnackbar } from 'notistack';

export function UserProfileClaimsDeleteDialog({
    tenant,
    itemId,
    customDeleteTitle,
    editingRow,
}: Readonly<DeleteDialogContentProps<{
    name: string,
    tenantId: string,
    claim: { id: string },
}>>) {
    const { claim, name } = editingRow ?? {};
    const { enqueueSnackbar } = useSnackbar();
    const dispatch = useAppDispatch();
    const currentEntityType = useAppSelector(selectCurrentEntityType);
    const currentTenantId = useAppSelector(selectDeleteCurrentTenantId);
    const userDetails = useAppSelector(selectUserDetails);
    const { firstName, lastName, userId } = userDetails ?? {};

    const { processing, handleDelete } = useDeleteDialogHandler({
        deleteThunk: deleteUserProfileClaims,
        entityName: 'Claim',
        successMessage: 'The Claim has been removed from this User under this Tenant.',
        onSuccess: () => {
            dispatch(setCurrentEntityType());
            updateUserProfileTenant({
                tenantId: tenant?.id ?? '',
                userId: userId ?? '',
            });
        },
        onError: () => {
            enqueueSnackbar(
                'There was an error while removing this Claim from this User.' +
                DEFAULT_ERROR_MESSAGE,
                { variant: 'error' }
            );
            return true;
        },
    });

    const deleteCallback = () => {
        if (itemId) {
            handleDelete({
                tenantId: tenant?.id ?? '',
                claimId: claim?.id ?? '',
                userId: userId ?? '',
            });
        }
    };

    if (currentEntityType !== DrawerEntitiesNames.UserProfileClaims || currentTenantId !== tenant?.id) {
        return null;
    }

    return  (
        <DeleteDialog deleteCallback={deleteCallback} processing={processing} customDeleteTitle={customDeleteTitle}>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Claim Name: <Typography component="span" color="text.primary" fontWeight="400">{name}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Name: <Typography component="span" color="text.primary" fontWeight="400">{`${firstName} ${lastName}`}</Typography>
            </Typography>
            <Typography color="info.light" fontSize='0.9rem' fontWeight='550'>
                Tenant Name: <Typography component="span" color="text.primary" fontWeight="400">{tenant?.name}</Typography>
            </Typography>
        </DeleteDialog>
    );
}
